import React, { useEffect, useRef } from 'react';

export interface KnockItem {
  knockId: string;
  guestSessionId: string;
  displayName: string;
  roomId: string;
  knockedAt: string;
}

interface KnockingToastProps {
  knocks: KnockItem[];
  onAdmit: (knockId: string, roomId: string) => void;
  onDeny: (knockId: string, roomId: string, reason?: string) => void;
  soundEnabled?: boolean;
}

let sharedAudioCtx: AudioContext | null = null;

export function playDoorbellChime() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!sharedAudioCtx) sharedAudioCtx = new AudioCtx();
    if (sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume().catch(() => {});
    }

    const now = sharedAudioCtx.currentTime;
    const osc1 = sharedAudioCtx.createOscillator();
    const osc2 = sharedAudioCtx.createOscillator();
    const gain = sharedAudioCtx.createGain();

    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.setValueAtTime(880, now); // A5
    osc2.frequency.setValueAtTime(659.25, now + 0.18); // E5

    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(sharedAudioCtx.destination);

    osc1.start(now);
    osc1.stop(now + 0.18);
    osc2.start(now + 0.18);
    osc2.stop(now + 0.7);
  } catch (err) {
    console.warn('[Doorbell] Audio blocked by browser policy:', err);
  }
}

// Global unlock on click or touch
if (typeof window !== 'undefined') {
  const unlock = () => {
    playDoorbellChime();
    window.removeEventListener('click', unlock);
  };
  window.addEventListener('click', unlock, { once: true });
}

export function KnockingNotificationToast({
  knocks,
  onAdmit,
  onDeny,
  soundEnabled = true,
}: KnockingToastProps) {
  const prevCountRef = useRef<number>(knocks.length);

  useEffect(() => {
    if (soundEnabled && knocks.length > prevCountRef.current) {
      playDoorbellChime();
    }
    prevCountRef.current = knocks.length;
  }, [knocks.length, soundEnabled]);

  if (knocks.length === 0) return null;

  return (
    <div className="fixed top-16 right-4 z-50 flex flex-col gap-2.5 max-w-sm w-full pointer-events-none">
      {knocks.map((k) => (
        <div
          key={k.knockId}
          className="pointer-events-auto p-4 bg-neutral-900/95 backdrop-blur border border-amber-500/50 rounded-xl shadow-2xl flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-3"
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="relative flex-shrink-0">
              <span className="w-3 h-3 block rounded-full bg-amber-400 animate-ping absolute inset-0 opacity-75" />
              <span className="w-3 h-3 block rounded-full bg-amber-400" />
            </div>
            <div className="truncate">
              <p className="text-xs font-bold text-white truncate">
                {k.displayName}
              </p>
              <p className="text-[11px] text-amber-300 font-mono">
                Knocking on #{k.roomId}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onAdmit(k.knockId, k.roomId)}
              className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg shadow transition"
            >
              Admit
            </button>
            <button
              onClick={() => onDeny(k.knockId, k.roomId, 'host_denied')}
              className="px-2.5 py-1.5 text-xs font-medium bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-lg transition"
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
